import React, { useState, useEffect } from 'react';
import { Tag, FileText, DollarSign, StickyNote } from 'lucide-react';
import Modal from '../ui/Modal';
import Button from '../ui/Button';
import TemplateDateInput from '../ui/TemplateDateInput';
import { useUpdateInvoiceTemplate } from '../../hooks/useInvoiceTemplates';
import { useLanguage } from '../../lang/LangContext';
import { KEYS } from '../../lang/keys';
import { normalizeFieldErrors } from '../../services/api';

const EditInvoiceTemplateModal = ({ isOpen, onClose, template }) => {
    const [formData, setFormData] = useState({
        label: '',
        name: '',
        amount: '',
        issueDate: null,
        dueDate: null,
        notes: '',
    });

    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState('');

    const updateTemplate = useUpdateInvoiceTemplate();
    const { t } = useLanguage();

    useEffect(() => {
        if (template) {
            setFormData({
                label: template.label || '',
                name: template.name || '',
                amount: template.amount?.toString() ?? '',
                issueDate: template.issue_date || null,
                dueDate: template.due_date || null,
                notes: template.notes || '',
            });
        }
    }, [template]);

    const validateForm = () => {
        const newErrors = {};

        if (!formData.label.trim()) {
            newErrors.label = t(KEYS.INVOICE_TEMPLATE_FIELD_LABEL_REQUIRED);
        } else if (formData.label.length > 191) {
            newErrors.label = t(KEYS.COMMON_ERROR_MAX_CHARS);
        }

        if (formData.name && formData.name.length > 191) {
            newErrors.name = t(KEYS.COMMON_ERROR_MAX_CHARS);
        }

        if (formData.amount !== '' && parseFloat(formData.amount) < 0) {
            newErrors.amount = t(KEYS.INVOICE_AMOUNT_ERROR);
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
        if (errors[name]) {
            setErrors((prev) => ({ ...prev, [name]: '' }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitError('');

        if (!validateForm()) return;

        setIsSubmitting(true);

        try {
            const data = {
                label: formData.label.trim(),
                name: formData.name || undefined,
                amount: formData.amount !== '' ? parseFloat(formData.amount) : undefined,
                issue_date: formData.issueDate || undefined,
                due_date: formData.dueDate || undefined,
                notes: formData.notes || undefined,
            };

            await updateTemplate.mutateAsync({ uid: template.uid, data });

            onClose();
        } catch (error) {
            if (error.code === 'VALIDATION_ERROR' && error.errors) {
                setErrors(normalizeFieldErrors(error.errors));
            } else {
                setSubmitError(error.message || t(KEYS.INVOICE_TEMPLATE_EDIT_ERROR));
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleClose = () => {
        if (!isSubmitting) {
            setErrors({});
            setSubmitError('');
            onClose();
        }
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={handleClose}
            title={t(KEYS.INVOICE_TEMPLATE_EDIT_TITLE)}
            subtitle="Update template details"
            size="medium"
            footer={
                <>
                    <Button variant="outline" size="medium" onClick={handleClose} disabled={isSubmitting}>
                        {t(KEYS.COMMON_CANCEL)}
                    </Button>
                    <Button variant="primary" size="medium" onClick={handleSubmit} disabled={isSubmitting}>
                        {isSubmitting ? t(KEYS.INVOICE_TEMPLATE_EDIT_SUBMITTING) : t(KEYS.INVOICE_TEMPLATE_EDIT_SUBMIT)}
                    </Button>
                </>
            }
        >
            {submitError && (
                <div className="p-3 bg-status-overdue-bg text-status-overdue rounded text-sm mb-4">
                    {submitError}
                </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-5">
                {/* Label */}
                <div className="space-y-1">
                    <label htmlFor="label" className="label-base flex items-center gap-1.5">
                        <Tag className="w-4 h-4" />
                        Label <span className="text-red-500">*</span>
                    </label>
                    <input
                        type="text"
                        id="label"
                        name="label"
                        value={formData.label}
                        onChange={handleChange}
                        className={`input-base ${errors.label ? 'border-red-500 focus:ring-red-500' : ''}`}
                        placeholder="e.g., Monthly Electricity"
                        maxLength={191}
                        disabled={isSubmitting}
                    />
                    {errors.label && (
                        <span className="text-xs text-red-500 mt-1">{errors.label}</span>
                    )}
                </div>

                {/* Name */}
                <div className="space-y-1">
                    <label htmlFor="name" className="label-base flex items-center gap-1.5">
                        <FileText className="w-4 h-4" />
                        Invoice Name
                    </label>
                    <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        className={`input-base ${errors.name ? 'border-red-500 focus:ring-red-500' : ''}`}
                        placeholder="e.g., Electricity Bill"
                        maxLength={191}
                        disabled={isSubmitting}
                    />
                    {errors.name && (
                        <span className="text-xs text-red-500 mt-1">{errors.name}</span>
                    )}
                </div>

                {/* Amount */}
                <div className="space-y-1">
                    <label htmlFor="amount" className="label-base flex items-center gap-1.5">
                        <DollarSign className="w-4 h-4" />
                        Default Amount
                    </label>
                    <input
                        type="number"
                        id="amount"
                        name="amount"
                        value={formData.amount}
                        onChange={handleChange}
                        className={`input-base ${errors.amount ? 'border-red-500 focus:ring-red-500' : ''}`}
                        placeholder="e.g., 149.99"
                        step="0.01"
                        min="0"
                        disabled={isSubmitting}
                    />
                    {errors.amount && (
                        <span className="text-xs text-red-500 mt-1">{errors.amount}</span>
                    )}
                </div>

                {/* Issue Date */}
                <TemplateDateInput
                    label="Issue Date"
                    value={formData.issueDate}
                    onChange={(val) => setFormData((prev) => ({ ...prev, issueDate: val }))}
                    disabled={isSubmitting}
                />

                {/* Due Date */}
                <TemplateDateInput
                    label="Due Date"
                    value={formData.dueDate}
                    onChange={(val) => setFormData((prev) => ({ ...prev, dueDate: val }))}
                    disabled={isSubmitting}
                />

                {/* Notes */}
                <div className="space-y-1">
                    <label htmlFor="notes" className="label-base flex items-center gap-1.5">
                        <StickyNote className="w-4 h-4" />
                        Notes
                    </label>
                    <textarea
                        id="notes"
                        name="notes"
                        value={formData.notes}
                        onChange={handleChange}
                        className="input-base resize-none"
                        rows={3}
                        placeholder="Optional notes..."
                        disabled={isSubmitting}
                    />
                </div>
            </form>
        </Modal>
    );
};

export default EditInvoiceTemplateModal;
